// src/pages/Templates/Template-Administrativo/Comercial/Standby/ComercialStandby.tsx
import React, { useCallback, useMemo, useState } from "react";
import Table from "../../../../../components/TableV2";
import TitleButtons from "../../../../../components/TitleButtons";
import { useStandbyComercial } from "./standbyData-comercial";
import ComercialStandbySidePanel from "./ComercialStandbySidePanel";

type Row = (string | number)[];

const IDX = {
  PV: 0,
  TIPO: 1,
  CODIGO: 2,
  NOME: 3,
  QTD: 4,
  CUSTO: 5,
  TECNICO: 6,
  DATA: 7,
  HORA: 8,
};

function normPV(value: unknown): string {
  return String(value ?? "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, " ");
}

function toStartOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function parseDateSmart(value: string): Date | null {
  if (!value) return null;

  const brMatch = /^(\d{2})\/(\d{2})\/(\d{4})/.exec(value.trim());

  if (brMatch) {
    const date = new Date(
      Number(brMatch[3]),
      Number(brMatch[2]) - 1,
      Number(brMatch[1])
    );

    return Number.isNaN(date.getTime()) ? null : date;
  }

  const parsed = new Date(value);

  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function diffDaysFromToday(dateStr: string): number | null {
  const date = parseDateSmart(dateStr);

  if (!date) return null;

  const diffMs =
    toStartOfDay(new Date()).getTime() - toStartOfDay(date).getTime();

  return Math.max(0, Math.floor(diffMs / 86400000));
}

const ComercialStandby: React.FC = () => {
  const headers = useMemo(
    () => [
      "PV",
      "Tipo",
      "Código",
      "Nome do Produto",
      "Quantidade",
      "Custo Unitário",
      "R. retirada",
      "Data",
      "Hora",
      "Dias de Atraso",
    ],
    []
  );

  const columnWidths = useMemo(
    () => [
      "60px",
      "80px",
      "70px",
      "300px",
      "10px",
      "100px",
      "120px",
      "80px",
      "60px",
      "70px",
    ],
    []
  );

  const {
    rows,
    loading,
    refresh,
    rawMovs,
    rawByProposta,
    erro,
  } = useStandbyComercial() as any;

  const [openPV, setOpenPV] = useState<string | null>(null);

  const displayRows: Row[] = useMemo(() => {
    if (!rows?.length) return rows || [];

    return (rows as Row[]).map((row) => {
      const pv = String(row[IDX.PV] ?? "-");

      const responsavelNome =
        rawByProposta?.[pv]?.mov?.responsavel_nome ||
        rawByProposta?.[pv]?.mov?.coletor_nome ||
        String(row[IDX.TECNICO] ?? "-");

      const clone = row.slice() as Row;

      clone[IDX.TECNICO] = responsavelNome || "-";

      const dataStr = String(clone[IDX.DATA] ?? "");
      const diasAtraso = diffDaysFromToday(dataStr);

      clone.push(diasAtraso === null ? "-" : String(diasAtraso));

      return clone;
    });
  }, [rows, rawByProposta]);

  const handleDoubleClick = useCallback(
    (rowIndex: number) => {
      const row = displayRows[rowIndex] as Row | undefined;

      if (!row) return;

      const pv = String(row[IDX.PV] ?? "").trim();

      if (!pv || pv === "-") return;

      setOpenPV(pv);
    },
    [displayRows]
  );

  const handleCloseSidePanel = useCallback(() => {
    setOpenPV(null);
  }, []);

  const selectedRawMovs = useMemo(() => {
    if (!openPV || !Array.isArray(rawMovs)) return [];

    const openNorm = normPV(openPV);

    return rawMovs.filter((mov: any) => {
      const proposta = String(mov?.movimentacao?.proposta_venda ?? "").trim();
      const os = String(mov?.movimentacao?.os_numero ?? "").trim();

      const chave = proposta && proposta.toLowerCase() !== "n/a" ? proposta : os;

      return normPV(chave) === openNorm;
    });
  }, [openPV, rawMovs]);

  return (
    <div>
      <TitleButtons
        fullBleedLine
        title="Comercial - Standby"
        buttons={[]}
      />

      {erro && (
        <div
          style={{
            marginBottom: 12,
            padding: "10px 12px",
            border: "1px solid #efb4b4",
            background: "#fff1f1",
            color: "#7f1d1d",
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          {erro}
        </div>
      )}

      {loading ? (
        <div style={{ padding: 16 }}>Carregando standby comercial…</div>
      ) : (
        <Table
          headers={headers}
          rows={displayRows}
          columnWidths={columnWidths}
          isTableStandby
          onDoubleClick={handleDoubleClick}
          atrasoColumnIndex={9}
          responsiveWidths={{
            1300: {
              1: "60px",
              2: "0px",
              3: "300px",
              4: "0px",
              5: "100px",
              6: "100px",
              7: "60px",
              8: "60px",
              9: "60px",
            },
            1024: {
              1: "60px",
              2: "0px",
              3: "260px",
              4: "0px",
              5: "96px",
              6: "110px",
              7: "70px",
              8: "0px",
              9: "64px",
            },
            768: {
              1: "70px",
              2: "0px",
              3: "220px",
              4: "0px",
              5: "0px",
              6: "100px",
              7: "70px",
              8: "0px",
              9: "62px",
            },
            520: {
              1: "68px",
              2: "0px",
              3: "200px",
              4: "0px",
              5: "0px",
              6: "0px",
              7: "70px",
              8: "0px",
              9: "60px",
            },
          }}
        />
      )}

      <ComercialStandbySidePanel
        open={!!openPV}
        propostaOuOs={openPV}
        rawMovs={selectedRawMovs}
        refresh={refresh}
        onClose={handleCloseSidePanel}
        title="Comercial - Standby"
      />
    </div>
  );
};

export default ComercialStandby;